import { useMemo, useCallback } from 'react';
import { Table as TanstackTable, Header } from '@tanstack/react-table';
import { generateGridTemplateColumns } from '@dealstack/frontend/shared/components/Table/MantineTable/utils/generateGridTemplate';
import { TableData } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';

interface UseTableGridLayoutOptions<T> {
	table: TanstackTable<T>;
	enableRowDragAndDrop: boolean;
	enableRowSelection: boolean;
	showRowSelection: boolean;
	enableRowActions: boolean;
	renderRowActions?: unknown;
	renderRowActionMenuItems?: unknown;
	minWidth: number;
	columnSizing?: Record<string, number>;
}

export interface UseTableGridLayoutReturn {
	gridTemplateColumns: string;
	gridColumnCount: number;
	getGridColumnForColumn: (columnId: string) => number;
	getGridColumnSpanForHeader: <T>(header: Header<T, unknown>) => number;
}

export function useTableGridLayout<T extends TableData>(
	options: UseTableGridLayoutOptions<T>
): UseTableGridLayoutReturn {
	const gridTemplateColumns = useMemo(() => {
		// Use leaf columns only (excludes parent/group columns)
		const allLeafColumns =
			typeof options.table.getAllLeafColumns === 'function'
				? options.table.getAllLeafColumns()
				: options.table.getVisibleFlatColumns();

		// Filter to only visible columns
		const visibleLeafColumns = allLeafColumns.filter((c) => c.getIsVisible());

		return generateGridTemplateColumns({
			columns: visibleLeafColumns.map((c) => ({
				id: c.id,
				columnDef: c.columnDef as ExtendedColumnDef<T, unknown>,
			})),
			enableRowDragAndDrop: options.enableRowDragAndDrop,
			enableRowSelection: options.enableRowSelection,
			showRowSelection: options.showRowSelection,
			enableRowActions: options.enableRowActions,
			renderRowActions: options.renderRowActions,
			renderRowActionMenuItems: options.renderRowActionMenuItems,
			minWidth: options.minWidth,
			columnSizing: options.columnSizing,
		});
	}, [
		options.table.getAllLeafColumns?.(),
		options.table.getVisibleFlatColumns(),
		options.enableRowDragAndDrop,
		options.enableRowSelection,
		options.showRowSelection,
		options.enableRowActions,
		options.renderRowActions,
		options.renderRowActionMenuItems,
		options.minWidth,
		options.columnSizing,
	]);

	const gridColumnCount = useMemo(() => {
		return gridTemplateColumns.split(' ').length;
	}, [gridTemplateColumns]);

	const getGridColumnForColumn = useCallback(
		(columnId: string) => {
			const allLeafColumns = options.table.getAllLeafColumns();

			const visibleLeafColumns = allLeafColumns.filter((c) => c.getIsVisible());
			const columnIndex = visibleLeafColumns.findIndex((c) => c.id === columnId);

			let offset = 1; // Grid columns are 1-indexed
			if (options.enableRowDragAndDrop) {
				offset++;
			}
			if (options.enableRowSelection && options.showRowSelection) {
				offset++;
			}

			return columnIndex + offset;
		},
		[
			options.table,
			options.enableRowDragAndDrop,
			options.enableRowSelection,
			options.showRowSelection,
		]
	);

	const getGridColumnSpanForHeader = useCallback(<T>(header: Header<T, unknown>) => {
		if (header.subHeaders && header.subHeaders.length > 0) {
			return header.subHeaders.filter((sh) => sh.column.getIsVisible()).length;
		}
		return 1;
	}, []);

	return {
		gridTemplateColumns,
		gridColumnCount,
		getGridColumnForColumn,
		getGridColumnSpanForHeader,
	};
}
